import * as CL from '@design-system/component-library';
import { Link } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables';
import { SubscriptionAdditionalServiceStatusEnum } from '../../common/enums.js';
import { noAdditionalServicesMsg, t } from '../../common/i18n';
import { CATEGORY_URL } from '../../common/utils/categoryUtils';
import { getRecurringPriceAsText } from '../../common/utils/priceUtils';
import { getSubscriptionUrlId } from '../../common/utils/subscriptionUtils';
import { filterAddOns } from '../OrderSubscriptionSelection/subscriptionChangeConfirmationUtils.js';
import { getAddOnStatus } from '../SubscriptionDetails/addOnDependencyUtils.js';
import { showAddonAsLink } from './subscriptionAddOnUtils.js';
import type { CategoryKey } from '../../common/utils/categoryUtils';
import type { Subscription } from '../../generated/api/subscription';
import type { SubscriptionDetails as SubscriptionDetailsModel } from '../../generated/api/subscriptionDetails';
import type { AddOnStatusInfo } from '../SubscriptionDetails/addOnDependencyUtils.js';
import type { ReactNode } from 'react';

import './SubscriptionAdditionalService.scss';

interface PriceInfo {
  value: ReactNode;
  additionalText?: ReactNode;
}

export type SubscriptionAdditionalServiceStatus = SubscriptionAdditionalServiceStatusEnum;

const ADD_ON_TYPE_SERVICE = 'SERVICE';

const getStatusColor = (serviceStatus: SubscriptionAdditionalServiceStatusEnum) => {
  switch (serviceStatus) {
    case SubscriptionAdditionalServiceStatusEnum.IN_USE:
      return 'ok';
    case SubscriptionAdditionalServiceStatusEnum.NOT_IN_USE:
      return 'secondary';
    case SubscriptionAdditionalServiceStatusEnum.CHANGE_REQUESTED:
      return 'warning';
  }
};

export interface SubscriptionAdditionalServiceProps {
  title: string;
  addOnStatusInfo?: AddOnStatusInfo;
  children?: ReactNode;
  priceInfo?: PriceInfo;
  subTitle?: ReactNode;
  additionalServicePath?: string;
  actionButton?: { buttonText: string; onClick: () => void };
  isEmployee?: boolean;
  isMobileId?: boolean;
  mobileIdLinkButton?: ReactNode;
}

const CommonAddonInfo = ({
  title,
  subTitle,
  addOnStatusInfo,
}: {
  title: string;
  subTitle?: ReactNode;
  addOnStatusInfo?: AddOnStatusInfo;
}) => (
  <div className={dsClass.TEXT_ALIGN_LEFT}>
    <div className={`${dsClass.DISPLAY_BLOCK}`}>{title}</div>
    {subTitle && <div className={dsClass.FONT_SIZE_SMALL}>{subTitle}</div>}
    {addOnStatusInfo && <CL.Badge type="status" color={getStatusColor(addOnStatusInfo.status)} />}
    {addOnStatusInfo && <span className={dsClass.FONT_SIZE_SMALL}>{` ${addOnStatusInfo.text}`}</span>}
  </div>
);

const AddonInfo = ({
  title,
  subTitle,
  isEmployee,
  additionalServicePath,
  addOnStatusInfo,
}: {
  title: string;
  addOnStatusInfo?: AddOnStatusInfo;
  isEmployee?: boolean;
  subTitle?: ReactNode;
  additionalServicePath?: string;
}) => {
  if (showAddonAsLink(additionalServicePath, isEmployee, addOnStatusInfo?.status)) {
    return (
      <Link className="of-additional-service__title" to={additionalServicePath!}>
        <CommonAddonInfo title={title} subTitle={subTitle} addOnStatusInfo={addOnStatusInfo} />
      </Link>
    );
  } else {
    return (
      <div className="of-additional-service__title">
        <CommonAddonInfo title={title} subTitle={subTitle} addOnStatusInfo={addOnStatusInfo} />
      </div>
    );
  }
};

// Show price info for admins, with EOE mobile id show link button
const PriceInfoOrLink = ({
  priceInfo,
  mobileIdLinkButton,
}: {
  priceInfo?: PriceInfo;
  mobileIdLinkButton: ReactNode;
}) => (
  <div className={`${dsClass.DISPLAY_FLEX} ${dsClass.MARGIN_TOP_2} of-additional-service__price`}>
    <div>
      <span className={`${dsClass.DISPLAY_BLOCK} ${dsClass.WHITE_SPACE_NOWRAP}`}>{priceInfo?.value}</span>
      {priceInfo?.additionalText && <span className={dsClass.FONT_SIZE_SMALL}>{priceInfo.additionalText}</span>}
      {mobileIdLinkButton && <span>{mobileIdLinkButton}</span>}
    </div>
  </div>
);

export const SubscriptionAdditionalService = ({
  subTitle,
  actionButton,
  addOnStatusInfo,
  children,
  title,
  priceInfo,
  additionalServicePath,
  isEmployee,
  isMobileId,
  mobileIdLinkButton,
}: SubscriptionAdditionalServiceProps) => {
  return (
    <div
      className={`of-additional-service ${
        addOnStatusInfo?.status === SubscriptionAdditionalServiceStatusEnum.NOT_IN_USE
          ? 'of-additional-service__inactive'
          : ''
      } of-additional-service ${dsClass.PADDING_3}`}
    >
      <div className={`of-additional-service__header ${dsClass.DISPLAY_FLEX} ${dsClass.JUSTIFY_CONTENT_SPACE_BETWEEN}`}>
        <AddonInfo
          title={title}
          addOnStatusInfo={addOnStatusInfo}
          subTitle={subTitle}
          isEmployee={isEmployee}
          additionalServicePath={additionalServicePath}
        />
        {(!isEmployee || isMobileId) && (
          <PriceInfoOrLink priceInfo={priceInfo} mobileIdLinkButton={mobileIdLinkButton} />
        )}
      </div>

      <div
        className={`of-additional-service__header ${dsClass.DISPLAY_FLEX} ${dsClass.JUSTIFY_CONTENT_SPACE_BETWEEN} ${dsClass.MARGIN_TOP_2}`}
      >
        <div className="of-additional-service__title">
          <div className={dsClass.TEXT_ALIGN_LEFT}>{children}</div>
        </div>
        {actionButton && (
          <div className={`${dsClass.DISPLAY_FLEX} ${dsClass.ALIGN_ITEMS_FLEX_END} of-additional-service__action`}>
            <CL.Button type="button" onClick={actionButton.onClick} color="link" size="s">
              {actionButton.buttonText}
            </CL.Button>
          </div>
        )}
      </div>
    </div>
  );
};

export const getSelectedAddonsContent = (
  details: SubscriptionDetailsModel,
  showStatus: boolean,
  category?: CategoryKey,
  subscription?: Subscription,
  search?: string,
  commercialProductName?: string,
  isRingSub: boolean = false
) => {
  const displayableAddOns = (details.selectedAddOns ?? []).filter(
    addon => addon.display === true && addon.addOnType === ADD_ON_TYPE_SERVICE
  );

  const filteredAddOnCodes = new Set(
    (commercialProductName
      ? filterAddOns(
          commercialProductName,
          displayableAddOns.map(addon => ({
            addOnCode: addon.addOnCode,
            addOnAssociationCode: addon.addOnCode,
            displayName: addon.addOnProductName,
          })),
          isRingSub
        )
      : displayableAddOns
    ).map(addOn => addOn.addOnCode)
  );

  const addons = displayableAddOns.filter(addon => filteredAddOnCodes.has(addon.addOnCode));

  if (addons.length === 0) {
    return <span>{t.EIYK(noAdditionalServicesMsg)}</span>;
  } else {
    return (
      <>
        {addons.map((e, i) => (
          <SubscriptionAdditionalService
            key={i}
            title={e.addOnProductName}
            addOnStatusInfo={showStatus ? getAddOnStatus(true) : undefined}
            isEmployee={false}
            additionalServicePath={
              category && subscription && search
                ? `${paths.PS_HOME}/${CATEGORY_URL[category]}/${getSubscriptionUrlId(subscription)}/${e.addOnCode + search}`
                : undefined
            }
            priceInfo={{ value: getRecurringPriceAsText(e.addOnMonthlyRecurringCharge) }}
          />
        ))}
      </>
    );
  }
};
